import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, updateUrl: String }

  create(event) {
    event.preventDefault()
    this.showContextModal()
  }

  async submitCreate(additionalContext) {
    this.showModal()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ additional_context: additionalContext })
      })

      const data = await response.json()

      if (response.ok && data.url) {
        this.showSuccess(data.url)
      } else {
        this.showError(data.error || "Failed to create Linear ticket.")
      }
    } catch (error) {
      this.showError("An unexpected error occurred. Please try again.")
    }
  }

  showContextModal() {
    const overlay = document.createElement("div")
    overlay.id = "linear-ticket-modal"
    overlay.className = "fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    overlay.innerHTML = `
      <div class="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-1">Create Linear Ticket</h3>
        <p class="text-sm text-gray-500 mb-4">Optionally add context or instructions for the AI when generating the ticket description.</p>
        <textarea id="linear-context-input" rows="4" placeholder="e.g. Suggest checking the auth middleware, don't refactor the existing flow, focus on the API layer..." class="block w-full text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 mb-4 p-3 resize-none"></textarea>
        <div class="flex justify-end gap-2">
          <button type="button" id="linear-context-cancel" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button type="button" id="linear-context-submit" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
            Create Ticket
          </button>
        </div>
      </div>
    `
    document.body.appendChild(overlay)

    document.getElementById("linear-context-cancel").addEventListener("click", () => {
      overlay.remove()
    })

    document.getElementById("linear-context-submit").addEventListener("click", () => {
      const context = document.getElementById("linear-context-input").value.trim()
      this.submitCreate(context)
    })

    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Escape") overlay.remove()
    })

    document.getElementById("linear-context-input").focus()
  }

  async update(event) {
    event.preventDefault()

    if (!confirm("Update Linear ticket with new comments, screenshots, and attachments?")) return

    this.showModal("Updating Linear Ticket", "Uploading new comments, screenshots, and attachments...")

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showSuccess(null, "Linear ticket has been updated with new details.")
      } else {
        this.showError(data.error || "No new updates to send to Linear.")
      }
    } catch (error) {
      this.showError("An unexpected error occurred. Please try again.")
    }
  }

  showModal(title = "Creating Linear Ticket", subtitle = "Analyzing issue and generating description with AI. This may take a moment...") {
    const overlay = document.createElement("div")
    overlay.id = "linear-ticket-modal"
    overlay.className = "fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    overlay.innerHTML = `
      <div class="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
        <div id="linear-modal-content">
          <svg class="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <h3 class="text-lg font-semibold text-gray-900 mb-1">${title}</h3>
          <p class="text-sm text-gray-500">${subtitle}</p>
        </div>
      </div>
    `
    document.body.appendChild(overlay)
  }

  showSuccess(url, message = "Linear ticket has been created successfully.") {
    const content = document.getElementById("linear-modal-content")
    if (!content) return

    const linkButton = url ? `
        <a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
          Open in Linear
          <svg class="ml-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>` : ""

    content.innerHTML = `
      <svg class="h-10 w-10 text-green-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h3 class="text-lg font-semibold text-gray-900 mb-1">Success</h3>
      <p class="text-sm text-gray-500 mb-4">${message}</p>
      <div class="flex flex-col gap-2">
        ${linkButton}
        <button onclick="document.getElementById('linear-ticket-modal').remove(); window.location.reload();" class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
          Close
        </button>
      </div>
    `
  }

  showError(message) {
    const content = document.getElementById("linear-modal-content")
    if (!content) return

    content.innerHTML = `
      <svg class="h-10 w-10 text-red-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h3 class="text-lg font-semibold text-gray-900 mb-1">Failed to Create Ticket</h3>
      <p class="text-sm text-gray-500 mb-4">${message}</p>
      <button onclick="document.getElementById('linear-ticket-modal').remove();" class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
        Close
      </button>
    `
  }
}
